import React, { useState, useEffect, memo } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import PrinterButton from "../../pages/Indent/IndentComponents/PrinterButton";
import ExportButton from "../../pages/Indent/IndentComponents/ExportButton"; // Import the new ExportButton component
import { IndentPDF } from "../Header/IndentPDF";
import { format } from "date-fns";

const DynamicTable = ({
  columns,
  data,
  selectedRow,
  onRowSelect,
  onDoubleClick,
  rowKey = "id",
  userIdKey = "userId",
  noDataMessage = "No data available",
  highlightColor = "bg-blue-100",
  hoverColor = "hover:bg-gray-50",
  userRole,
  mobileView = false,
}) => {
  const [clickTimeout, setClickTimeout] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [pdfError, setPdfError] = useState("");
  const rowsPerPage = 10;
  const userId = useSelector((state) => state.auth.id);

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      // Handle both ISO and MySQL datetime formats
      const date = new Date(dateString.replace(' ', 'T'));
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString);
        return "N/A";
      }
      return format(date, "dd/MM/yy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  // Debug log for incoming props
  useEffect(() => {
    console.log("DynamicTable props:", {
      columns,
      dataLength: data?.length,
      userRole,
      userId
    });
  }, [columns, data, userRole, userId]);

  // Modified filtering logic to handle Store Officer role correctly
  const displayData = userRole?.toLowerCase() === "admin"
    ? data
    : userRole?.toLowerCase() === "store officer"
      ? data // Remove the filter since the API already filters by SOID
      : data;

  const totalPages = Math.ceil(displayData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = displayData.slice(startIndex, startIndex + rowsPerPage);

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleRowClick = (row) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      if (onDoubleClick) {
        onDoubleClick(row);
      }
    } else {
      const timeoutId = setTimeout(() => {
        if (onRowSelect) {
          onRowSelect(row);
        }
        setClickTimeout(null);
      }, 200);
      setClickTimeout(timeoutId);
    }
  };

  const getRowBackgroundColor = (row) => {
    if (!row) return "";
    
    if (row.Status?.toLowerCase() === "closed" || 
        row.Status?.toLowerCase() === "force closed") {
      return "bg-green-100";
    }

    if (row.L1 === "Approved" &&
        row.L2 === "Approved" &&
        row.PO === "Approved" &&
        row.PH === "Approved" &&
        row.IR === "Approved") {
      return "bg-green-100";
    }

    return selectedRow?.[rowKey] === row[rowKey] ? highlightColor : "";
  };

  const fetchIndentData = async (indent) => {
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/indents/${indent.IndentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/indentitems/${indent.IndentID}`
    );
    const itemsData = await itemsResponse.json();

    // Fetch status information
    const statusResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/status/${indent.IndentID}`
    );
    const statusData = await statusResponse.json();
    const status = Array.isArray(statusData) ? statusData[0] : statusData;

    return {
      indentId: indent.IndentID,
      creationDate: formatDate(indentDetails?.CreationDate),
      PEApprovalDate: formatDate(indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate),
      L1ApprovedDate: formatDate(indentDetails?.L1ApprovedDate),
      L2ApprovalDate: formatDate(indentDetails?.L2ApprovalDate),
      projectName: indentDetails?.ProjectName || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      status: indentDetails?.Status || "Active",
      invoicingType: indentDetails?.TypeName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      // Add approval flags
      PO: status?.PO || indentDetails?.PO || 0,
      L1: status?.L1 || indentDetails?.L1 || 0,
      L2: status?.L2 || indentDetails?.L2 || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        quantityReceived: item.QuantityReceived,
        difference: item.Difference,
        remarks: item.Remarks,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: formatDate(item.CommitedDate),
        so: item.IR,
        ph: item.PH,
      })),
    };
  };

  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  // Mobile card view
  const MobileRow = memo(({ row }) => {
    const isExpanded = expandedRows.has(row[rowKey]);

    const renderValue = (column, value) => {
      if (column.render) {
        return column.render(row);
      }
      if (column.key === "CreationDate" || 
          column.key === "PEApprovalDate" || 
          column.key === "L1ApprovedDate" || 
          column.key === "L2ApprovalDate" || 
          column.key === "CommitedDate") {
        return formatDate(value);
      }
      return value || 'N/A';
    };

    return (
      <div
        className={clsx(
          "border rounded-lg mb-2 p-3",
          getRowBackgroundColor(row),
          hoverColor
        )}
        onClick={() => handleRowClick(row)}
      >
        <div className="flex justify-between items-center">
          <div className="font-medium">ID: {row.IndentID}</div>
          <div className="flex items-center gap-2">
            <ExportButton
              getPDFComponent={getPDFComponent}
              filename={`indent-${row.IndentID}`}
              fetchData={() => fetchIndentData(row)}
              onError={(error) => setPdfError("Failed to generate export. Please try again.")}
              className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors duration-200"
              iconSize={16}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpansion(row[rowKey]);
              }}
              className="p-1 rounded-full hover:bg-gray-200"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-600 mt-1">
          Status: {row.Status || 'N/A'}
        </div>

        {isExpanded && (
          <div className="mt-2 space-y-1 text-sm">
            {columns.map(column => (
              column.key !== "IndentID" && column.key !== "Status" && (
                <div key={column.key} className="flex justify-between">
                  <span className="font-medium">{column.label}:</span>
                  <span>{renderValue(column, row[column.key])}</span>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    );
  });

  // Pagination controls
  const PaginationControls = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between px-2 sm:px-4 py-3 space-y-3 sm:space-y-0">
      <div className="text-sm text-gray-700">
        Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, displayData.length)} of{" "}
        {displayData.length} entries
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className={clsx(
            "inline-flex items-center rounded px-2 sm:px-3 py-1 sm:py-2",
            "border border-gray-300 bg-white text-sm font-medium",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
            currentPage === 1
              ? "cursor-not-allowed text-gray-400"
              : "text-gray-700 hover:bg-gray-50"
          )}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className={clsx(
            "inline-flex items-center rounded px-2 sm:px-3 py-1 sm:py-2",
            "border border-gray-300 bg-white text-sm font-medium",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
            currentPage === totalPages
              ? "cursor-not-allowed text-gray-400"
              : "text-gray-700 hover:bg-gray-50"
          )}
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // Return empty div if no data
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        {noDataMessage}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mobile View */}
      {mobileView && (
        <div className="block sm:hidden">
          <div className="space-y-2">
            {paginatedData.map((row) => (
              <MobileRow key={row[rowKey]} row={row} />
            ))}
          </div>
        </div>
      )}
      {/* Desktop View */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100 text-gray-800">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="border border-gray-300 px-4 py-2 text-left text-sm sm:text-base font-semibold"
                >
                  {column.label}
                </th>
              ))}
              <th className="border border-gray-300 px-4 py-2 text-left text-sm sm:text-base font-semibold">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((row) => (
              <tr
                key={row[rowKey]}
                className={clsx(
                  "cursor-pointer transition-colors duration-150",
                  getRowBackgroundColor(row),
                  hoverColor,
                  "border-b border-gray-300"
                )}
                onClick={() => handleRowClick(row)}
              >
                {columns.map((column) => (
                  <td
                    key={`${row[rowKey]}-${column.key}`}
                    className="border border-gray-300 px-4 py-2 text-sm sm:text-base"
                  >
                    {column.render ? column.render(row) : 
                     (column.key === "CreationDate" || 
                      column.key === "PEApprovalDate" || 
                      column.key === "L1ApprovedDate" || 
                      column.key === "L2ApprovalDate" || 
                      column.key === "CommitedDate") ? formatDate(row[column.key]) :
                     row[column.key]}
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-sm sm:text-base">
                  <div className="flex items-center gap-2">
                    <ExportButton
                      getPDFComponent={getPDFComponent}
                      filename={`indent-${row.IndentID}`}
                      fetchData={() => fetchIndentData(row)}
                      onError={(error) => setPdfError("Failed to generate export. Please try again.")}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
                      iconSize={20}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls />
    </div>
  );
};

export default React.memo(DynamicTable);